<template>
  <Toast ref="toastRef" :position="position" :group="group" :life="life" :showCloseIcon="showClose"
    :baseZIndex="baseZIndex" @show="onShow" @close="onClose" @clear-all="onClearAll" @click="onClick" :class="[
      customClass,
      {
        'p-toast-rtl': rtl,
        [`p-toast-${size}`]: size
      }
    ]" />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'

export default defineComponent({
  name: 'ONEToast',
  components: {
    Toast
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { ONEToastProps, ONEToastMessage } from './types'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'

const props = withDefaults(defineProps<ONEToastProps>(), {
  position: 'top-right',
  showClose: true,
  life: 3000,
  customClass: '',
  // Must stay undefined, not an empty string. PrimeVue Toast only renders a message
  // when its own group matches message.group, and toast.add() without a group emits
  // group === undefined. With the old default of '' the comparison never matched, so
  // every ungrouped toast in the app was silently dropped and users saw no feedback
  // on save, delete or validation failure.
  group: undefined,
  size: 'normal',
  rtl: false,
  'aria-label': 'Notifications',
  baseZIndex: 1000,
  enableTransitions: true,
  showErrors: true,
  validateOnMount: false,
  formContext: true,
  disabled: false
})

const emit = defineEmits<{
  'show': [message: ONEToastMessage]
  'close': [message: ONEToastMessage]
  'clear-all': []
  'click': [message: ONEToastMessage]
  'validation-error': [errors: string[]]
  'validation-success': []
}>()

// Refs and State
const toastRef = ref<InstanceType<typeof Toast> | null>(null)
const localValidationErrors = ref<string[]>([])
const isValidating = ref(false)

// Composables
const { formState } = useFormContext()
const { t } = useLocalization()
const toast = useToast()

// Computed
const currentErrors = computed(() => {
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    return error
  })
})

// Methods
const getDefaultIcon = (severity?: string) => {
  switch (severity) {
    case 'success':
      return 'pi pi-check'
    case 'info':
      return 'pi pi-info-circle'
    case 'warn':
      return 'pi pi-exclamation-triangle'
    case 'error':
      return 'pi pi-times'
    default:
      return 'pi pi-info-circle'
  }
}

const validate = async (): Promise<boolean> => {
  if (!props.validationRules?.length) return true

  isValidating.value = true
  try {
    const errors: string[] = []

    for (const rule of props.validationRules) {
      if (rule.validator) {
        const isValid = await rule.validator('')
        if (!isValid && rule.message) {
          errors.push(rule.message)
        }
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

// Event Handlers
const onShow = (message: ONEToastMessage) => {
  emit('show', message)
}

const onClose = (message: ONEToastMessage) => {
  emit('close', message)
}

const onClearAll = () => {
  emit('clear-all')
}

const onClick = (message: ONEToastMessage) => {
  emit('click', message)
}

// Public Methods
const show = (message: ONEToastMessage) => {
  if (props.disabled) return
  toast.add(message)
}

const clear = () => {
  toast.removeAllGroups()
}

defineExpose({
  show,
  clear,
  validate,
  validationErrors: currentErrors,
  isValidating,
  isValid: computed(() => currentErrors.value.length === 0)
})
</script>
